import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Lock, User } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { useAuth } from '@/context/AuthContext';
import * as authApi from '@/api/auth';
import { isApiError } from '@/api/errors';
import CenteredBox from '@/features/auth/CenteredBox';
import { AuthField, AuthForm } from '@/features/auth/AuthForm';
import { loginErrorKey, pickTagline, replaceExclamations } from '@/features/auth/leprosorium';

const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const { t, i18n } = useTranslation();
    const { setUser, isAuthenticated, isLoaded } = useAuth();
    const navigate = useNavigate();
    const [loginError, setLoginError] = useState('');
    // One tagline per visit. The draw is kept rather than the line, so
    // switching the interface language mid-visit gives the same line in the
    // other language instead of a new one.
    const [draw] = useState(Math.random);

    // Redirect if user is already authenticated
    useEffect(() => {
        if (isLoaded && isAuthenticated) {
            navigate('/books/page/1');
        }
    }, [isAuthenticated, isLoaded, navigate]);

    // Don't render the form if user is authenticated or still loading
    if (!isLoaded || isAuthenticated) {
        return null;
    }

    const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // The submit button stays enabled on purpose: a disabled button gives a
        // reader nothing to press and no reason why, whereas submitting says it.
        if (!username || !password) {
            setLoginError(t('usernameAndPasswordRequired'));
            return;
        }

        setLoginError('');

        try {
            const userData = await authApi.login({ username, password });

            // The login response already carries the account, so there is no
            // reason to ask the server who just signed in.
            setUser({
                username: userData.username,
                first_name: userData.first_name,
                last_name: userData.last_name,
                is_superuser: userData.is_superuser,
                books_lang: userData.books_lang,
                have_favs: userData.have_favs,
            });
            navigate('/books/page/1');
        } catch (error) {
            if (isApiError(error) && error.status === 403) {
                const message = (error.body as { message?: string } | undefined)?.message;
                setLoginError(t(loginErrorKey(message)));
                return;
            }
            console.error('Error logging in:', error);
        }
    };

    return (
        <CenteredBox>
            <AuthForm
                title={t('welcomeBack')}
                subtitle={pickTagline(i18n.language, () => draw)}
                error={loginError}
                onSubmit={handleLogin}
                submitLabel={t('loginButton')}
                secondaryAction={
                    // Registration is invite-only, but it is no secret: the
                    // way there sits beside the forgotten password, in the
                    // same quiet style, rather than behind an easter egg.
                    <div className="flex w-full justify-between">
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            onClick={() => navigate('/forgot-password')}
                            className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                        >
                            {t('forgotPassword')}
                        </Button>
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            onClick={() => navigate('/registration')}
                            className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                        >
                            {t('registration')}
                        </Button>
                    </div>
                }
            >
                <AuthField
                    id="login-username"
                    name="username"
                    label={t('username')}
                    icon={User}
                    autoComplete="username"
                    value={username}
                    // The blog's parser, applied as the reader types.
                    onChange={(value) => setUsername(replaceExclamations(value))}
                />
                <AuthField
                    id="login-password"
                    name="password"
                    label={t('password')}
                    icon={Lock}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={setPassword}
                />
            </AuthForm>
        </CenteredBox>
    );
};

export default Login;
